import React from 'react';

/**
 * Injects a provider into a component, allowing for additional props to be passed to the Provider at render time.
 * @param Provider - The Provider component to inject.
 * @param providerProps - Default props to pass to the Provider.
 * @returns A function that takes a component and returns a new component with the Provider injected.
 */
export function withProvider<T extends React.ComponentType<any>>(
  Provider: T,
  providerProps: Omit<React.ComponentProps<T>, 'children'> = {} as any,
) {
  return (WrappedComponent: React.ComponentType<any>) => {
    // Return a new component that renders the Provider and WrappedComponent
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    const providerName = Provider.displayName || Provider.name || 'Provider';

    const WithProvider = React.forwardRef((props: any, ref) => {
      // We can use the React.cloneElement method to merge additional props
      // with the providerProps at the time of using the HOC, if necessary.
      return (
        <Provider {...(providerProps as any)}>
          <WrappedComponent ref={ref} {...props} />
        </Provider>
      );
    });

    WithProvider.displayName = `${providerName}(${displayName})`;

    return WithProvider;
  };
}
